import {QueryParameters} from "../../../../common/queryParameters";
import {PageScrollbar} from "../../../../common/pageScrollbar";
import {Resolution} from "../../../../common/resolution";
import {BOOTSTRAP} from "../../../../common/resolutionConstants";
import {Dictionary} from "../../../../page/elements/dictionary";
import {Deferred, EOP_ERRORS, Promises, schedule} from "../../../../common/utils/promises";
import {resolve} from "../../../../container";
import {type Filters, SearchFacade} from "../searchFacade";
import type {FilterToggleEvent} from "./filterValue";
import {customElement, property, query, state} from "lit/decorators.js";
import {html, LitElement, type TemplateResult} from "lit";
import Styles from "./searchFilter.lit.scss";
import {EopSearchFilterBlock, Filter} from "./filterBlock";
import {EventBus} from "../../../../common/eventBus";
import {FILTER_TOGGLE_EVENT, SEARCH_TILES_IN_VIEWPORT_EVENT} from "../constants";
import {ManagingResources} from "../../../../common/lifetime";
import {installEscapeHandlerOn, onTabPressAfter} from "../../../../common/utils/events";
import {trackingButtonInteract} from "../../../../tracking/trackingAttributes";

const SEARCH_FILTER_QUERY_PARAMETER_PREFIX = "filter_";
const RESULT_COUNT_PLACEHOLDER = "[COUNT]";

class FilterBlock {
    public constructor(
        public category: string,
        public filters: Filter[],
        public label: string,
        public disabled: boolean
    ) {
    }
}

@customElement("eop-search-filter")
export class EopSearchFilter extends ManagingResources(LitElement) {

    @state()
    private resultsLabel: string;
    @state()
    private filterBlocks: FilterBlock[];
    @state()
    private isUpToDate: boolean = true;
    @property({attribute: "open", type: Boolean, reflect: true})
    private open: boolean = false;

    @query(".filter-container")
    private filterContainer: HTMLElement;

    public static readonly styles = Styles;

    private dictionary: Dictionary;
    private totalCount: number;
    private countUpdater: Deferred<number>;

    public constructor(
        private resolution: Resolution = resolve(Resolution),
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        private eventBus: EventBus = resolve(EventBus),
        private searchFacade: SearchFacade = resolve(SearchFacade),
        private promises: Promises = resolve(Promises),
        private queryParameters: QueryParameters = resolve(QueryParameters)
    ) {
        super();

        this.filterBlocks = [];
        this.totalCount = 0;
        this.countUpdater = new Deferred();
        searchFacade.registerFilterInitialization(async (sf) => {
            this.queryParameters.onStringsStartingWith(SEARCH_FILTER_QUERY_PARAMETER_PREFIX, filters => {
                const initialFilters: Filters = new Map();

                filters.forEach((values, category) => {
                    initialFilters.set(category, Array.from(new Set(values ?? [])));
                });

                sf.addFilters(initialFilters);
            });
            await sf.fetchFilters();
            return sf;
        });
    }

    public render(): TemplateResult {
        const resetLabel = this.dictionary.translate("CONTENT_SEARCH_RESET");
        const searchLabel = this.dictionary.translate("CONTENT_SEARCH_FILTER");
        const closeLabel = this.dictionary.translate("MSG_CLOSE_DIALOG");
        return html`
            <div class="search-filter">
                <div class="shadow-overlay"></div>
                <div class="filter-container">
                    <button type="button" class="filter-button secondary"
                            @click=${this.toggleFilterPanel}
                            ${trackingButtonInteract(searchLabel)}
                    >${searchLabel}</button>
                    <div class="filter-panel">
                        <button class="close-filter" aria-label=${closeLabel}
                                @click=${this.closeFilter}
                                ${trackingButtonInteract(closeLabel)}
                        ></button>
                        <button class="reset-button"
                                ?disabled=${!this.isResettable()}
                                @click=${this.reset}
                                ${trackingButtonInteract(resetLabel)}
                        >${resetLabel}</button>
                        ${(this.renderFilterBlocks())}
                        <div class="filter-panel-footer">
                            <button type="button" class="search-button primary"
                                    ?disabled=${this.isUpToDate}
                                    @click=${this.triggerSearch}
                                    ${trackingButtonInteract(this.resultsLabel)}
                            >${this.resultsLabel}</button>
                            <eop-overlay-image-spinner event="result-count"></eop-overlay-image-spinner>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private renderFilterBlocks(): TemplateResult[] {
        return this.filterBlocks.map(block => {
            return html`
                <eop-search-filter-block
                        .category=${block.category}
                        .label=${this.dictionary.translate(block.label)}
                        .filters=${block.filters}
                        ?disabled=${block.disabled}
                ></eop-search-filter-block>
            `;
        });
    }

    public lastFocusableElement(): HTMLElement {
        const searchButton = this.renderRoot.querySelector<HTMLButtonElement>(".search-button");
        if (searchButton && !searchButton.disabled) {
            return searchButton;
        }
        const filterButtons = Array.from(this.renderRoot.querySelectorAll<EopSearchFilterBlock>("eop-search-filter-block"))
            .filter(block => !block.hasAttribute("disabled"))
            .flatMap(block => block.focusableElements());
        if (filterButtons.isNotEmpty()) {
            return filterButtons.last()!;
        }
        return this.renderRoot.querySelector<HTMLButtonElement>(".close-filter")!;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.searchFacade.onUpdateFilter((_, totalCount) => {
            this.updateCount(totalCount);
            this.updateFilterBlocks();
        });
        this.searchFacade.onAppliedFilter(filters => this.persistFilters(filters));

        this.searchFacade.onChangeGroup(() => {
            this.updateFilterBlocks();

            this.updateTotalCount().then(() => {
                this.resultsLabel = this.getShowResultsLabel();
            });
        });

        this.updateFilterBlocks();
        this.dictionary = Dictionary.of(this);
        this.resolution.onBootstrapBreakpointChange(() => this.reactToBreakpointChange(), this);
        this.resultsLabel = this.getShowResultsLabel();

        this.addEventListener(FILTER_TOGGLE_EVENT, (event: Event) => {
            const eventDetails = (event as FilterToggleEvent).detail;
            void this.toggleFilter(eventDetails.category, eventDetails.value);
        });
        this.eventBus.on(SEARCH_TILES_IN_VIEWPORT_EVENT, event => {
            this.classList.toggle("tiles-in-viewport", event.data.inViewport);
        }, this);

        installEscapeHandlerOn(this, () => this.closeFilter());
    }

    private updateFilterBlocks(): void {
        this.filterBlocks = this.getFilterBlocks();
    }

    private async toggleFilterPanel(): Promise<void> {
        this.open = !this.open;
        this.updateScrollBehaviour();

        if (this.open) {
            onTabPressAfter(() => this.lastFocusableElement(), () => this.closeFilter());
            this.resetToAppliedState();
            this.isUpToDate = !this.isChanged();
            this.filterBlocks = this.getFilterBlocks();
            await this.updateTotalCount();
            this.resultsLabel = this.getShowResultsLabel();
        }
    }

    public async toggleFilter(category: string, value: string): Promise<void> {
        this.searchFacade.toggleFilter(category, value);
        await this.updateResultCount();
        this.updateProperties();
    }

    public async triggerSearch(): Promise<void> {
        await schedule(this.searchFacade.commitFilters()).as("trigger-search");
        this.closeFilter();
    }

    private closeFilter(): void {
        this.open = false;
        this.updateScrollBehaviour();
    }

    public async reset(): Promise<void> {
        await schedule(this.internalReset()).as("filter-reset");
        this.updateProperties();
    }

    private async internalReset(): Promise<void> {
        await this.searchFacade.resetFilters();
        await this.updateResultCount();
    }

    private updateProperties(): void {
        this.filterBlocks = this.getFilterBlocks();
        this.resultsLabel = this.getShowResultsLabel();
        this.isUpToDate = !this.isChanged();
    }

    private getShowResultsLabel(): string {
        const totalCount = this.getTotalCount();
        const subkey = totalCount === 1 ? "1" : "_";
        return this.dictionary.translate("CONTENT_SEARCH_RESULTS_SHOW", subkey, {[RESULT_COUNT_PLACEHOLDER]: totalCount});
    }

    private updateScrollBehaviour(): void {
        if (this.resolution.downTo(BOOTSTRAP.MD)) {
            return;
        }
        if (this.open) {
            this.scrollFilterContainerToBottom();
            this.pageScrollbar.disablePageScrollability();
        } else {
            this.pageScrollbar.enablePageScrollability();
        }
    }

    private scrollFilterContainerToBottom(): void {
        this.filterContainer.scrollTop = this.filterContainer.scrollHeight;
    }

    private reactToBreakpointChange(): void {
        if (!this.open) {
            return;
        }
        if (this.resolution.upTo(BOOTSTRAP.SM)) {
            this.scrollFilterContainerToBottom();
            this.pageScrollbar.disablePageScrollability();
        } else {
            this.pageScrollbar.enablePageScrollability();
        }
    }

    public getFilterBlocks(): FilterBlock[] {
        const allFilterData = this.searchFacade.getAllFilterData() ?? [];
        const selectedFilters = this.searchFacade.getSelectedFilters();
        const applicableFilterData = this.searchFacade.applicableFilterData();

        return allFilterData.map(data => {
            const filters = data.filters.map(filter => {
                const isActive = (selectedFilters.get(data.id) ?? []).includes(filter);
                const isDisabled = applicableFilterData.every(applicableData => !applicableData.filters.includes(filter));
                return new Filter(filter, isActive, isDisabled);
            });
            const isBlockDisabled = applicableFilterData.every(applicableData => applicableData.id !== data.id);
            return new FilterBlock(data.id, filters, data.label, isBlockDisabled);
        });
    }

    public getTotalCount(): number {
        return this.totalCount;
    }

    public async updateTotalCount(): Promise<void> {
        await this.updateResultCount();
    }

    public resetToAppliedState(): void {
        this.searchFacade.revertFilters();
    }

    public isResettable(): boolean {
        return this.isChanged() || this.searchFacade.getSelectedFilters().size > 0;
    }

    public isChanged(): boolean {
        return this.searchFacade.hasUncommittedFilters();
    }

    private updateCount(totalCount: number): void {
        this.totalCount = Math.max(totalCount, 0);
    }

    private async updateResultCount(): Promise<void> {
        const filters = this.searchFacade.getSelectedFilters();
        this.countUpdater.rejectExpected();
        const deferred = new Deferred<number>();
        this.countUpdater = deferred;

        const totalCountPromise = deferred.promise
            .then(totalCount => this.totalCount = totalCount)
            .catch(EOP_ERRORS);

        this.searchFacade.requestActiveResultsCount(filters)
            .then(response => deferred.resolve(response));
        const spinnerForResultCount = this.promises.decoratorFor("result-count")(totalCountPromise);
        await schedule(spinnerForResultCount).as("update-result-count");
    }

    private persistFilters(filters: Filters): void {
        this.queryParameters.removeParametersStartingWith(SEARCH_FILTER_QUERY_PARAMETER_PREFIX);
        filters.forEach((values, category) => {
            this.queryParameters.appendAll(category, values);
        });
    }
}